import { Checkbox, FormControlLabel, Paper, Typography, Divider } from '@mui/material';
import { MemreFlexBox } from 'components/core';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';

interface GraduatedDifficultyProps {
  itemId: string;
}

export default function GraduatedDifficulty({ itemId }: GraduatedDifficultyProps) {
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch the current state of the graduated difficulty setting
  useEffect(() => {
    const fetchGraduatedDifficultyStatus = async () => {
      if (!itemId) return;

      try {
        setIsLoading(true);
        const response = await axios.get(`/api/v3/items/${itemId}`);
        const normalizedData = normalizeJSONAPIResponse(response.data);

        // Check if graduated difficulty is enabled
        setIsEnabled(!!normalizedData.graduateDifficultyEnabled);
      } catch (error) {
        console.error('Error fetching graduated difficulty status', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGraduatedDifficultyStatus();
  }, [itemId]);

  // Handle toggling the graduated difficulty setting
  const handleToggleGraduatedDifficulty = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const checked = event.target.checked;

      // Update in database
      await axios.patch(`/api/v3/items/${itemId}`, {
        data: {
          attributes: {
            'graduate-difficulty-enabled': checked,
          },
        },
      });

      // Update local state
      setIsEnabled(checked);
    } catch (error) {
      console.error('Error updating graduated difficulty setting', error);
      // Revert back to previous state if there's an error
      setIsEnabled(!event.target.checked);
    }
  };

  if (isLoading) {
    return (
      <MemreFlexBox direction="column" gap={2} sx={{ mt: 2 }}>
        <Typography>Loading...</Typography>
      </MemreFlexBox>
    );
  }

  return (
    <MemreFlexBox direction="column" gap={2} sx={{ mt: 2 }}>
      <Typography variant="subtitle1">Graduated Difficulty</Typography>
      <Typography variant="body2" color="textSecondary">
        Cerego can dynamically adjust the difficulty of test material based on previous behavior.
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          padding: 2,
        }}
      >
        <FormControlLabel
          control={<Checkbox checked={isEnabled} onChange={handleToggleGraduatedDifficulty} color="primary" />}
          label="Enable Graduated Difficulty"
        />
      </Paper>
    </MemreFlexBox>
  );
}
